import React, { Component } from "react";
import { withRouter } from "react-router-dom";
import PropTypes from "prop-types";
import withStyles from "@material-ui/core/styles/withStyles";
import CircularProgress from "@material-ui/core/CircularProgress";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogFooter from "components/Dialog/DialogFooter.jsx";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import InputLabel from "@material-ui/core/InputLabel";
import FormControl from "@material-ui/core/FormControl";
import Icon from "@material-ui/core/Icon";

import GridContainer from "components/Grid/GridContainer.jsx";
import GridItem from "components/Grid/GridItem.jsx";
import Button from "components/CustomButtons/Button.jsx";
import CustomInput from "components/CustomInput/CustomInput.jsx";
import CustomDatePicker from "components/DatePicker/CustomDatePicker.jsx";
import DialogHeader from "components/Dialog/DialogHeader.jsx";
import DataTable, { Pill } from "components/DataTable/DataTable.jsx";

import api from "api/index";
import ViewToolbar from "components/Toolbar/ViewToolbar.jsx";

const styles = {
  header: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  formControl: { width: "100%", marginTop: "16px" },
  errorMsg: { color: "#f44336", fontSize: "13px" },
  searchBar: { display: "flex", gap: "12px", marginBottom: "16px", alignItems: "flex-end", flexWrap: "wrap" }
};

const ITEM_STATUSES = ["Active", "Inactive", "Maintenance", "Disposed"];
const DEPLOYMENT_STATUSES = ["Deployed", "In Store", "Maintenance"];

const STATUS_TONES = {
  Active: "success",
  Inactive: "muted",
  Maintenance: "warning",
  Disposed: "danger"
};

const DEPLOY_TONES = {
  Deployed: "info",
  "In Store": "success",
  Maintenance: "warning"
};

const BLANK = {
  name: "", serialnumber: "", manufacturer: "",
  status: "Active", deploymentstatus: "In Store", deploymentdate: ""
};

class InventoryList extends Component {
  state = {
    items: [],
    loading: true,
    search: "",
    statusFilter: "",
    open: false,
    editing: null,
    form: { ...BLANK },
    saving: false,
    error: null
  };

  componentDidMount() {
    this.fetchItems();
  }

  fetchItems = async () => {
    this.setState({ loading: true });
    try {
      const { search, statusFilter } = this.state;
      const params = { limit: 100 };
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      const res = await api.get("/inventory", { params });
      const d = res.data.data || res.data;
      this.setState({ items: Array.isArray(d) ? d : d.data || [], loading: false });
    } catch (e) {
      this.setState({ loading: false });
    }
  };

  handleChange = field => e => {
    const v = e.target.value;
    this.setState(prev => ({ form: { ...prev.form, [field]: v } }));
  };

  handleSave = async () => {
    const { form, editing } = this.state;
    if (!form.name) {
      this.setState({ error: "Item name is required." });
      return;
    }
    this.setState({ saving: true, error: null });
    try {
      if (editing) {
        await api.put(`/inventory/${editing}`, form);
      } else {
        await api.post("/inventory", form);
      }
      this.setState({ open: false, saving: false, form: { ...BLANK } });
      this.fetchItems();
    } catch (e) {
      this.setState({ error: e.response?.data?.message || "Failed to save.", saving: false });
    }
  };

  handleDelete = async (id) => {
    if (!window.confirm("Remove this inventory item? This cannot be undone.")) return;
    try {
      await api.delete(`/inventory/${id}`);
      this.fetchItems();
    } catch (e) { /* silent */ }
  };

  openEdit = (item) => {
    this.setState({
      open: true,
      editing: item.id,
      error: null,
      form: {
        name: item.name || "",
        serialnumber: item.serialnumber || "",
        manufacturer: item.manufacturer || "",
        status: item.status || "Active",
        deploymentstatus: item.deploymentstatus || "In Store",
        deploymentdate: item.deploymentdate ? item.deploymentdate.substring(0, 10) : ""
      }
    });
  };

  render() {
    const { classes } = this.props;
    const { items, loading, search, statusFilter, open, editing, form, saving, error } = this.state;

    const columns = [
      { id: "name", header: "Item Name", accessorFn: item => item.name || "—" },
      { id: "serialnumber", header: "Serial No", accessorFn: item => item.serialnumber || "—", meta: { variant: "mono" } },
      { id: "manufacturer", header: "Manufacturer", accessorFn: item => item.manufacturer || "—" },
      {
        id: "status",
        header: "Status",
        cell: info => {
          const item = info.row.original;
          return <Pill tone={STATUS_TONES[item.status] || "muted"}>{item.status || "Unknown"}</Pill>;
        }
      },
      {
        id: "deploymentstatus",
        header: "Deployment Status",
        cell: info => {
          const item = info.row.original;
          return <Pill tone={DEPLOY_TONES[item.deploymentstatus] || "muted"}>{item.deploymentstatus || "—"}</Pill>;
        }
      },
      { id: "deploymentdate", header: "Deployment Date", accessorFn: item => item.deploymentdate ? item.deploymentdate.substring(0, 10) : "—" },
      {
        id: "actions",
        header: "",
        cell: info => {
          const item = info.row.original;
          return (
            <div style={{ display: "flex", gap: "4px" }}>
              <Button size="sm" color="info" simple onClick={() => this.openEdit(item)}>Edit</Button>
              <Button size="sm" color="danger" simple onClick={() => this.handleDelete(item.id)}>Del</Button>
            </div>
          );
        }
      }
    ];

    return (
      <GridContainer>
        <GridItem xs={12}>
          <ViewToolbar tabs={[]} actions={[]} />
          <div className={classes.searchBar}>
            <input
              type="text"
              placeholder="Search items..."
              value={search}
              onChange={e => this.setState({ search: e.target.value })}
              onKeyDown={e => e.key === "Enter" && this.fetchItems()}
              style={{ flex: 1, maxWidth: "280px", padding: "8px 12px", border: "1px solid #ddd", borderRadius: "4px", fontSize: "14px" }}
            />
            <FormControl style={{ minWidth: "150px" }}>
              <InputLabel>Status</InputLabel>
              <Select value={statusFilter} onChange={e => this.setState({ statusFilter: e.target.value }, this.fetchItems)}>
                <MenuItem value="">— All —</MenuItem>
                {ITEM_STATUSES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </Select>
            </FormControl>
            <Button size="sm" onClick={this.fetchItems} color="primary" style={{ marginBottom: "2px" }}>
              Search
            </Button>
          </div>

          <DataTable
            title="Inventory"
            subtitle="Institutional inventory items and deployment status"
            actions={
              <Button size="sm" color="primary" onClick={() => this.setState({ open: true, editing: null, form: { ...BLANK }, error: null })}>
                <Icon style={{ marginRight: "4px" }}>add</Icon> Add Item
              </Button>
            }
            columns={columns}
            data={items}
            isLoading={loading}
            emptyState="No inventory items found."
          />
        </GridItem>

        <Dialog open={open} onClose={() => this.setState({ open: false })} maxWidth="sm" fullWidth>
          <DialogHeader title={editing ? "Edit Inventory Item" : "Add Inventory Item"} />
          <DialogContent>
            {error && <p className={classes.errorMsg}>{error}</p>}
            <CustomInput
              labelText="Item Name *"
              id="invItemName"
              formControlProps={{ fullWidth: true }}
              inputProps={{ value: form.name, onChange: this.handleChange("name") }}
            />
            <CustomInput
              labelText="Serial Number"
              id="invSerial"
              formControlProps={{ fullWidth: true }}
              inputProps={{ value: form.serialnumber, onChange: this.handleChange("serialnumber"), placeholder: "e.g. SN-123456" }}
            />
            <CustomInput
              labelText="Manufacturer"
              id="invManufacturer"
              formControlProps={{ fullWidth: true }}
              inputProps={{ value: form.manufacturer, onChange: this.handleChange("manufacturer"), placeholder: "e.g. Dell, HP..." }}
            />
            <GridContainer>
              <GridItem xs={6}>
                <FormControl className={classes.formControl}>
                  <InputLabel>Status</InputLabel>
                  <Select value={form.status} onChange={this.handleChange("status")}>
                    {ITEM_STATUSES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                  </Select>
                </FormControl>
              </GridItem>
              <GridItem xs={6}>
                <FormControl className={classes.formControl}>
                  <InputLabel>Deployment Status</InputLabel>
                  <Select value={form.deploymentstatus} onChange={this.handleChange("deploymentstatus")}>
                    {DEPLOYMENT_STATUSES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                  </Select>
                </FormControl>
              </GridItem>
            </GridContainer>
            <CustomDatePicker
              labelText="Deployment Date"
              id="invDeployDate"
              formControlProps={{ fullWidth: true }}
              inputProps={{ value: form.deploymentdate, onChange: this.handleChange("deploymentdate") }}
            />
          </DialogContent>
          <DialogFooter>
            <Button outlined onClick={() => this.setState({ open: false })} disabled={saving}>Cancel</Button>
            <Button color="primary" onClick={this.handleSave} disabled={saving}>
              {saving ? <CircularProgress size={18} style={{ color: "#fff" }} /> : "Save"}
            </Button>
          </DialogFooter>
        </Dialog>
      </GridContainer>
    );
  }
}

InventoryList.propTypes = { classes: PropTypes.object.isRequired };
export default withRouter(withStyles(styles)(InventoryList));
